import React, { useState, useEffect, createContext } from 'react';
import './App.css';
import Header from './Header/Header';
import TabComponent from './TabComponent/TabComponent';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Col, Row, Container, Collapse, Button } from 'react-bootstrap'
import initialState from './Forms/formInitialState'
import modelContext from './modelContext';
import VisualizeModel from './VisualizeModel/VisualizeModel';
import { annotSpecies } from './types';
import { validate_env, validate_comparts, validate_annot_species, validate_species, validate_reactions, validate_events } from './xml_validate'
import { schema } from './schema';
//import cors from 'cors';
export const showFormContext = createContext({
	showForm: false,
	changeFormDisplay: (formDisplay: boolean) => { }
})

function App() {
	// context variable deciding if the form should show or not
	const [showForm, setShowForm] = useState(false);
	const changeFormDisplay = (formDisplay: boolean) => {
		setShowForm(formDisplay);
	}

	// hold the internal data of the model with modelJson
	// get copy of the initial state
	const [modelJson, setModelJson] = useState(JSON.parse(JSON.stringify(initialState)));

	const appendModelJson = (key: string, new_state: any, overwrite: Boolean = false) => {
		setModelJson({ ...modelJson, [key]: new_state })
	}

	// state variables that hold data dependencies: compartment list, annotation species list, list of metabolites, list of molecule complexes (species)
	const [compartList, setCompartList] = useState(Array<string>());
	const [annotSpeciesList, setAnnotSpeciesList] = useState({});
	const [speciesList, setSpeciesList] = useState(Array<string>());
	const [metabList, setMetabList] = useState(Array<string>());

	// keep track of model status, which of the tabs in modelJson has errors (can arise from changing tabs that are dependencies of other tabs)
	const [modelStatus, setModelStatus] = useState(Array(6).fill(true));

	// when model is changed, update accompanying lists
	useEffect(() => {
		updateComparts();
		updateAnnot();
		updateSpecies()
	}, [modelJson])

	// hold string array of compartment names, used for annotSpecies, species, reactions
	const updateComparts = (compartments = modelJson?.["listOfCompartments"]?.["compartment"]) => {
		if (!compartments) return;
		let list = compartments.map((compart: any, index: number) => {
			let id: string = compart._attributes.id;
			return (id);
		});
		setCompartList(list);
	}

	// by default, writes the annot list based on the annotSpecies of modelJson. Can optionally insert a custom one
	const writeAnnotList = function (annotSpecies = modelJson?.["annotSpecies"]?.["cell4d:speciesType"]): { [key: string]: annotSpecies } {
		let list: { [key: string]: annotSpecies } = {};
		if (annotSpecies) {
			annotSpecies.forEach((species: any, index: number) => {
				let id: string = species._attributes.id;
				let reduced_sites: Array<{ id: string, states: Array<string> }> = [];

				if (species?.["cell4d:listOfBindingSites"]?.["cell4d:bindingSite"]) {
					reduced_sites = species["cell4d:listOfBindingSites"]["cell4d:bindingSite"].map((site: any, index: number) => {
						let site_id: string = site._attributes.id;
						let states: Array<string> = [];
						if (site?.["cell4d:listOfPossibleStates"]?.["cell4d:state"]) {
							states = site["cell4d:listOfPossibleStates"]["cell4d:state"].map((state: any, index: number) => {
								return (state._attributes.value);
							})
						}
						return ({
							id: site_id,
							states: states
						})
					})
				}
				list[id] = {
					id: id,
					sites: reduced_sites
				};
			});
		}
		return (list);
	}

	// custom setter that updates the annot species list and optionally metab list, if they exist
	// annot species list is formatted as annot string name key, annotSpecies value
	const updateAnnot = (annotSpecies = modelJson?.["annotSpecies"]?.["cell4d:speciesType"]) => {
		if (!annotSpecies) return;
		// create the annotation species list
		let list: { [key: string]: annotSpecies } = writeAnnotList(annotSpecies);
		setAnnotSpeciesList(list);

		// if there are any metabs, add to the list. otherwise add the blank list
		// fill metab list if it is a bulk molecule
		let metab_list: Array<string> = [];
		if (annotSpecies) {
			annotSpecies.forEach((species: any, index: number) => {
				let id: string = species._attributes.id;
				if (species?._attributes?.speciesMoleculeType === "SIMPLE_MOLECULE") {
					metab_list.push(id);
				}
			});
		}

		setMetabList(metab_list);
	}

	// custom setter that updates speciesList from the saved modelJson object
	const updateSpecies = (species = modelJson?.["listOfSpecies"]?.["species"]) => {
		if (!species) return;
		let speciesList: Array<string> = species.map((species: any) => {
			let id: string = species._attributes.id;
			return (id)
		});
		setSpeciesList(speciesList);
	}

	const validateModel = (inputComparts = compartList, inputAnnots = annotSpeciesList, inputMetabs = metabList, inputSpecies = speciesList): Array<Boolean> => {

		// change the input annot (string key: annotSpecies obj value) to an array of annotSpecies
		let annotSpeciesArray = Object.values(inputAnnots);

		// let envValidation = validate_env(modelJson["cell4d:environmentVariables"], schema.envVar);
		let compartValidation = validate_comparts(modelJson.listOfCompartments, schema.compart);
		let annotValidation = validate_annot_species(modelJson.annotSpecies, inputComparts, schema.annotSpecies);
		let speciesValidation = validate_species(modelJson.listOfSpecies, inputComparts, annotSpeciesArray, schema.species);
		let reactionValidation = validate_reactions(modelJson.listOfReactions, inputComparts, annotSpeciesArray, inputMetabs, schema.reactions);
		let eventValidation = validate_events(modelJson.listOfEvents, inputComparts, inputSpecies, schema.events);

		let newStatusArray = Array(6).fill(true);
		// if (!envValidation.pass) newStatusArray[0] = false;
		if (!compartValidation.pass) newStatusArray[1] = false;
		if (!annotValidation.pass) newStatusArray[2] = false;
		if (!speciesValidation.pass) newStatusArray[3] = false;
		if (!reactionValidation.pass) newStatusArray[4] = false;
		if (!eventValidation.pass) newStatusArray[5] = false;

		return (newStatusArray);
	}

	const [showVis, setShowVis] = useState(false);


	return (
		<div className="App">
			<modelContext.Provider value={{ modelJson, setModelJson, appendModelJson, modelStatus, setModelStatus, validateModel, compartList, updateComparts, annotSpeciesList, updateAnnot, speciesList, updateSpecies, metabList, writeAnnotList }}>
				<showFormContext.Provider value={{ showForm, changeFormDisplay }}>
					<Header header="Cell4D Model Editor" description="A Cell4D model file editor" />
					<Container fluid>
						<Row>
							{/* Sidebar help doc placeholder */}
							<Col md={2}>
							{/* <pre>{JSON.stringify(modelStatus, null, 2)}</pre> */}
							</Col>

							{/* main tab component of the program, each tab representing one section of the XML model document */}
							<Col md={7}>
								{showForm ? <TabComponent envForm={modelJson} /> : null}
							</Col>

							{/* show some representation of model file, json/xml */}
							<Col md={3}>
								<br/>
								{showForm ? <Button size="sm" variant="outline-secondary" onClick={() => {setShowVis(!showVis)}}>{showVis ? "Hide JSON data" : "Show JSON data"}</Button> : null}
								<br/>
								<br/>
								{showForm && showVis ? <VisualizeModel /> : null}
							</Col>
						</Row>
					</Container>
				</showFormContext.Provider>
			</modelContext.Provider>

		</div>
	);
}

export default App;
